// Single source of truth for the /compare hub cards and every page's
// "related comparisons" block. Both used to be duplicated per page and drifted
// out of sync — change a title or ship a new page here, in ONE place, instead.
//
// Scope note: Song7 competes on the chord-chart + setlist job, not presentation.
// We deliberately don't list presentation-first tools (ProPresenter, OpenLP,
// OpenSong, EasyWorship) here — adding one would misrepresent the category.
//
// Shipping a comparison page: add an entry below with `comingSoon: false` and a
// `to`. (Set `comingSoon: true` / `to: null` to tease one before it's written.)

export interface ComparePage {
  slug: string
  title: string
  // Short label for tight spots like the footer, where the full `title` wraps.
  // Keep the ranking keyword ("alternative"/"companion") — just trim the brand
  // prefix — so footer anchor text still reinforces the search term. Falls back
  // to `title` when omitted.
  navLabel?: string
  summary: string
  to: string | null
  comingSoon: boolean
  // Written but not ready to surface: the page file still exists and is reachable
  // by URL, but it's excluded from the hub, footer, and related blocks. Flip to
  // false (or remove) to list it.
  hidden?: boolean
}

// Every comparison, in display order.
export const comparePages: ComparePage[] = [
  {
    slug: 'paper-binders',
    title: 'Paper binder & Google Docs alternative',
    navLabel: 'Paper binder alternative',
    summary: 'Move your team off printed charts, PDFs and shared docs, and keep what paper does well.',
    to: '/compare/paper-binders',
    comingSoon: false,
  },
  {
    slug: 'planning-center-music-stand',
    title: 'Planning Center Music Stand alternative',
    navLabel: 'Music Stand alternative',
    summary: "A cross-platform, all-in-one option next to Planning Center's stage-reading add-on.",
    to: '/compare/planning-center-music-stand',
    comingSoon: false,
  },
  {
    slug: 'onsong',
    title: 'OnSong alternative',
    navLabel: 'OnSong alternative',
    summary: "A cross-platform, team-first option next to OnSong's Apple-only chord charts.",
    to: '/compare/onsong',
    comingSoon: false,
  },
  {
    slug: 'worshiptools-charts',
    title: 'WorshipTools Charts alternative',
    summary: "A focused, paid tool next to WorshipTools' free, catalog-backed chord chart app.",
    to: '/compare/worshiptools-charts',
    comingSoon: false,
    hidden: true, // Needs a content pass before we surface it again.
  },
  {
    slug: 'songselect',
    title: 'CCLI SongSelect companion',
    navLabel: 'CCLI SongSelect',
    summary: 'The setlist, live-sync, and presentation layer for the songs you license through CCLI SongSelect.',
    to: '/compare/songselect',
    comingSoon: false,
  },
]

// Every comparison we currently surface (hub cards, footer, related blocks all
// read this so a `hidden` page disappears from every listing at once).
export const visibleComparePages: ComparePage[] = comparePages.filter((p) => !p.hidden)

// Hub link shown first in each page's related block.
export const allComparisonsCard: ComparePage = {
  slug: 'all',
  title: 'All Song7 comparisons',
  summary: 'Every Song7 comparison in one place.',
  to: '/compare',
  comingSoon: false,
}

// Related block for a page: the hub link, then every surfaced comparison except
// the current one (a page reached directly while hidden still gets the visible set).
export const getRelatedComparePages = (currentSlug: string): ComparePage[] => [
  allComparisonsCard,
  ...visibleComparePages.filter((p) => p.slug !== currentSlug),
]
